import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';

export interface WorkspacePhoneState {
  workflowIdByCell: Record<string, string>;
  runningColumns: string[];
  bulkRunsByBatchId: Record<
    string,
    { userUploadId: string; columnName: string; total: number | null; completed: number }
  >;
}

const initialState: WorkspacePhoneState = {
  workflowIdByCell: {},
  runningColumns: [],
  bulkRunsByBatchId: {},
};

export function phoneCellKey(docId: string, columnName: string): string {
  return `${docId}:${columnName}`;
}

const workspacePhoneSlice = createSlice({
  name: 'workspacePhone',
  initialState,
  reducers: {
    phoneWorkflowAdopted(state, action: PayloadAction<{ cellKey: string; workflowId: string }>) {
      state.workflowIdByCell[action.payload.cellKey] = action.payload.workflowId;
    },
    phoneColumnRunStarted(state, action: PayloadAction<string>) {
      if (!state.runningColumns.includes(action.payload)) state.runningColumns.push(action.payload);
    },
    phoneColumnRunFinished(state, action: PayloadAction<string>) {
      state.runningColumns = state.runningColumns.filter((name) => name !== action.payload);
    },
    phoneBulkStarted(
      state,
      action: PayloadAction<{
        bulkBatchId: string;
        userUploadId: string;
        columnName: string;
        total?: number | null;
      }>
    ) {
      const prev = state.bulkRunsByBatchId[action.payload.bulkBatchId];
      state.bulkRunsByBatchId[action.payload.bulkBatchId] = {
        userUploadId: action.payload.userUploadId,
        columnName: action.payload.columnName,
        total: action.payload.total ?? prev?.total ?? null,
        completed: prev?.completed ?? 0,
      };
    },
    phoneBulkTick(state, action: PayloadAction<string>) {
      const entry = state.bulkRunsByBatchId[action.payload] ?? {
        userUploadId: '',
        columnName: '',
        total: null,
        completed: 0,
      };
      entry.completed += 1;
      if (entry.total !== null && entry.completed >= entry.total) {
        delete state.bulkRunsByBatchId[action.payload];
        return;
      }
      state.bulkRunsByBatchId[action.payload] = entry;
    },
    phoneBulkCleared(state, action: PayloadAction<string>) {
      delete state.bulkRunsByBatchId[action.payload];
    },
    phoneRunFlagsCleared(state) {
      state.runningColumns = [];
    },
  },
});

export const {
  phoneWorkflowAdopted,
  phoneColumnRunStarted,
  phoneColumnRunFinished,
  phoneBulkStarted,
  phoneBulkTick,
  phoneBulkCleared,
  phoneRunFlagsCleared,
} = workspacePhoneSlice.actions;

export const selectPhoneWorkflowId = (state: RootState, cellKey: string): string | undefined =>
  state.workspacePhone.workflowIdByCell[cellKey];

export const selectIsPhoneColumnRunning = (state: RootState, columnName: string): boolean =>
  state.workspacePhone.runningColumns.includes(columnName);

export const selectBulkRun = (
  state: RootState,
  sheetId: string,
  columnName: string
): { userUploadId: string; columnName: string; total: number | null; completed: number } | undefined => {
  const runs = state.workspacePhone.bulkRunsByBatchId;
  for (const batchId of Object.keys(runs)) {
    const run = runs[batchId];
    if (run.userUploadId === sheetId && run.columnName === columnName) return run;
  }
  return undefined;
};

export const selectIsBulkColumnRunning = (state: RootState, sheetId: string, columnName: string): boolean =>
  selectBulkRun(state, sheetId, columnName) !== undefined;

export default workspacePhoneSlice.reducer;
